import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { GitBranch, Search, ChevronRight, CheckCircle2, Play, Plus } from 'lucide-react'
import { isAxiosError } from 'axios'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
} from '@/components/ui'

interface Template {
  id: string
  name: string
  description: string | null
  category: string | null
  scope: 'base' | 'industry' | 'customer'
  status: string
  step_count: number
}

const CATEGORY_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  offboarding: 'Offboarding',
  incident: 'Incident',
  change: 'Change',
  approval: 'Freigabe',
  audit: 'Audit',
}

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: Template
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border-2 p-4 transition-all ${
        selected
          ? 'border-brand-500 bg-brand-50'
          : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {template.category && (
              <span className="text-xs font-medium text-brand-600 bg-brand-50 border border-brand-100 rounded px-1.5 py-0.5">
                {CATEGORY_LABELS[template.category] ?? template.category}
              </span>
            )}
            {template.scope !== 'base' && (
              <span className="text-xs text-neutral-500 bg-neutral-100 rounded px-1.5 py-0.5">
                {template.scope === 'industry' ? 'Branche' : 'Kunde'}
              </span>
            )}
          </div>
          <div className="font-semibold text-neutral-900">{template.name}</div>
          {template.description && (
            <div className="mt-1 text-sm text-neutral-500 line-clamp-2">{template.description}</div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-lg font-semibold text-neutral-800">{template.step_count}</div>
          <div className="text-xs text-neutral-400">Schritte</div>
        </div>
      </div>
      {selected && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-brand-600 font-medium">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Ausgewählt
        </div>
      )}
    </button>
  )
}

export function ProcessStartPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')

  const { data: templates, isLoading, error, refetch } = useQuery({
    queryKey: ['prozesse-templates-active'],
    queryFn: () =>
      apiClient
        .get<Template[]>('/prozesse/templates', { params: { status: 'active' } })
        .then((r) => r.data),
    retry: false,
  })

  function getErrorDetails(err: unknown): string {
    if (isAxiosError(err)) {
      const status = err.response?.status
      const data = err.response?.data
      const body = typeof data === 'object' ? JSON.stringify(data) : String(data ?? '')
      return `HTTP ${status ?? '?'}: ${err.message}${body ? `\n${body}` : ''}`
    }
    return err instanceof Error ? err.message : String(err)
  }

  const startMutation = useMutation({
    mutationFn: (body: { templateId: string; title: string; dueDate?: string }) =>
      apiClient.post<{ id: string }>('/prozesse/instances', body).then((r) => r.data),
    onSuccess: (data) => {
      navigate(`/prozesse/${data.id}/wizard`)
    },
  })

  const filtered = templates?.filter((t) => {
    const q = search.toLowerCase()
    return (
      !q ||
      t.name.toLowerCase().includes(q) ||
      (t.category ?? '').toLowerCase().includes(q) ||
      (t.description ?? '').toLowerCase().includes(q)
    )
  }) ?? []

  function handleSelect(t: Template) {
    setSelectedTemplate(t)
    if (!title) setTitle(t.name)
  }

  function handleStart() {
    if (!selectedTemplate || !title.trim()) return
    startMutation.mutate({
      templateId: selectedTemplate.id,
      title: title.trim(),
      dueDate: dueDate || undefined,
    })
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl">
      <PageHeader
        title="Neuer Prozess"
        description="Wählen Sie eine Vorlage und konfigurieren Sie den neuen Prozess."
        breadcrumbs={[
          { label: 'Prozesse', to: '/prozesse' },
          { label: 'Neuer Prozess' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template selection */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <Card.Header>
              <div className="font-semibold text-neutral-800">1. Vorlage auswählen</div>
              <div className="text-sm text-neutral-500">
                {isLoading
                  ? 'Lade Vorlagen…'
                  : `${templates?.length ?? 0} aktive Vorlage${templates?.length !== 1 ? 'n' : ''} verfügbar`}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Vorlage suchen…"
                  className="pl-9"
                />
              </div>

              {isLoading && <LoadingState label="Vorlagen werden geladen…" />}
              {error && (
                <ErrorState
                  title="Fehler beim Laden"
                  description="Vorlagen konnten nicht geladen werden."
                  details={getErrorDetails(error)}
                  onRetry={() => refetch()}
                />
              )}
              {!isLoading && !error && filtered.length === 0 && (
                <EmptyState
                  icon={<GitBranch className="w-7 h-7" />}
                  title="Keine aktiven Vorlagen"
                  description="Erstellen und aktivieren Sie zuerst eine Prozessvorlage."
                  action={
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate('/prozesse/templates')}
                    >
                      <Plus className="w-4 h-4 mr-1.5" />
                      Vorlage erstellen
                    </Button>
                  }
                />
              )}
              {!isLoading && !error && filtered.length > 0 && (
                <div className="space-y-2">
                  {filtered.map((t) => (
                    <TemplateCard
                      key={t.id}
                      template={t}
                      selected={selectedTemplate?.id === t.id}
                      onSelect={() => handleSelect(t)}
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Configuration panel */}
        <div className="space-y-4">
          <Card>
            <Card.Header>
              <div className="font-semibold text-neutral-800">2. Konfigurieren</div>
            </Card.Header>
            <Card.Body className="space-y-4">
              <Input
                label="Titel"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Onboarding Max Mustermann"
                hint="Eindeutiger Name für diesen Prozess"
                disabled={!selectedTemplate}
              />
              <Input
                label="Fälligkeitsdatum (optional)"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={!selectedTemplate}
              />

              {selectedTemplate && (
                <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-3 text-sm space-y-1">
                  <div className="font-medium text-neutral-700">{selectedTemplate.name}</div>
                  <div className="text-neutral-500 flex items-center gap-1">
                    <GitBranch className="w-3.5 h-3.5" />
                    {selectedTemplate.step_count} Schritt{selectedTemplate.step_count !== 1 ? 'e' : ''}
                  </div>
                </div>
              )}
            </Card.Body>
            <Card.Footer>
              <Button
                variant="primary"
                className="w-full"
                disabled={!selectedTemplate || !title.trim() || startMutation.isPending}
                onClick={handleStart}
              >
                {startMutation.isPending ? (
                  'Wird gestartet…'
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-1.5" />
                    Prozess starten
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
              {startMutation.error && (
                <p className="mt-2 text-xs text-red-600">
                  {startMutation.error instanceof Error
                    ? startMutation.error.message
                    : 'Fehler beim Starten'}
                </p>
              )}
            </Card.Footer>
          </Card>
        </div>
      </div>
    </div>
  )
}
